"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import styles from "./SoftwareFeatures.module.css";
import { useServiceData } from "../../hooks/useServiceData";

const BRAND = "#F48428";

const STATIC_LEFT = [
  {
    icon: "fa-regular fa-pen-to-square",
    title: "SaaS applications",
    text: "Robust SaaS tailored to your business needs. Streamline processes, boost efficiency and scale with your growth.",
  },
  {
    icon: "fa-solid fa-globe",
    title: "Web‑based portals",
    text: "Seamless user experiences for collaboration and data sharing across your organisation.",
  },
  {
    icon: "fa-solid fa-shield",
    title: "Custom B2B software",
    text: "Automation and operational improvements with solutions built around your workflows.",
  },
];

const STATIC_RIGHT = [
  {
    icon: "fa-solid fa-mobile-screen",
    title: "Cross‑platform mobile apps",
    text: "Consistent UX on multiple devices while optimising cost and time‑to‑market.",
  },
  {
    icon: "fa-brands fa-windows",
    title: "Windows applications",
    text: "Optimise internal processes with efficient, user‑friendly desktop applications.",
  },
  {
    icon: "fa-regular fa-clone",
    title: "White‑label software",
    text: "High‑quality, customisable software you can brand and deploy as your own.",
  },
];

function getFallbackIcon(title = "") {
  const lower = title.toLowerCase();
  if (lower.includes("rate") || lower.includes("price") || lower.includes("cost") || lower.includes("pricing")) {
    return "fa-solid fa-tags";
  }
  if (lower.includes("quality") || lower.includes("excellence") || lower.includes("standard")) {
    return "fa-solid fa-award";
  }
  if (lower.includes("expertise") || lower.includes("team") || lower.includes("house") || lower.includes("staff")) {
    return "fa-solid fa-user-gear";
  }
  if (lower.includes("security") || lower.includes("privacy") || lower.includes("confidential") || lower.includes("ip")) {
    return "fa-solid fa-shield-halved";
  }
  if (lower.includes("delivery") || lower.includes("time") || lower.includes("schedule") || lower.includes("on-time")) {
    return "fa-solid fa-clock";
  }
  if (lower.includes("flexib") || lower.includes("model") || lower.includes("engagement") || lower.includes("t&m")) {
    return "fa-solid fa-sliders";
  }
  return "fa-solid fa-circle-check";
}

function FeatureItem({ icon, title, text }) {
  const renderIconContent = () => {
    let imageUrl = null;
    let iconClass = null;

    if (typeof icon === "string" && icon.trim()) {
      if (icon.startsWith("http") || icon.startsWith("/") || icon.match(/\.(png|jpg|jpeg|svg|webp)$/i)) {
        imageUrl = icon;
      } else {
        iconClass = icon;
      }
    } else if (icon && typeof icon === "object" && icon.url) {
      imageUrl = icon.url;
    }

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={title || "feature icon"}
          style={{ width: 22, height: 22, objectFit: "contain" }}
        />
      );
    }

    if (iconClass) {
      return <i className={iconClass} style={{ fontSize: 18 }}></i>;
    }

    const fallback = getFallbackIcon(title);
    return <i className={fallback} style={{ fontSize: 18 }}></i>;
  };

  return (
    <div className={`${styles.featureItem} d-flex align-items-start gap-3 mb-3`}>
      <div
        className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: 44,
          height: 44,
          borderRadius: 999,
          color: BRAND,
          background: `linear-gradient(180deg, ${BRAND}22, transparent)`,
          border: `1px solid ${BRAND}55`,
          boxShadow: `0 0 0 6px ${BRAND}10`,
        }}
      >
        {renderIconContent()}
      </div>
      <div>
        <div className="fw-bold text-white" style={{ lineHeight: 1.1 }}>{title}</div>
        <div className={`${styles.featureText} small mt-1`}>{text}</div>
      </div>
    </div>
  );
}

export default function SoftwareFeatures() {
  const { data: backendPageData } = useServiceData();
  const backendItems = backendPageData?.features?.items;

  // Use backend data if available, otherwise use static fallback
  let LEFT = STATIC_LEFT;
  let RIGHT = STATIC_RIGHT;
  let sectionTitle = "Personalized Solutions for Your Unique Business Needs";
  let sectionSubtitle = "Comprehensive Features to Drive Your Success.";
  let eyebrow = "Features";

  if (backendItems && backendItems.length > 0) {
    const mid = Math.ceil(backendItems.length / 2);
    LEFT = backendItems.slice(0, mid).map(item => {
      const iconVal = typeof item.icon === "string" ? item.icon : item.icon?.url;
      return {
        icon: iconVal || getFallbackIcon(item.title),
        title: item.title,
        text: item.description,
      };
    });
    RIGHT = backendItems.slice(mid).map(item => {
      const iconVal = typeof item.icon === "string" ? item.icon : item.icon?.url;
      return {
        icon: iconVal || getFallbackIcon(item.title),
        title: item.title,
        text: item.description,
      };
    });
    sectionTitle = backendPageData?.features?.subtitle || sectionTitle;
    eyebrow = backendPageData?.features?.title || eyebrow;
  }

  return (
    <section className={`py-5 position-relative ${styles.softwareFeatures}`} style={{ background: "rgba(0,0,0,0)" }}>
      <div className="container">
        <div className={`text-center mb-4 ${styles.sectionHead}`}>
          <div className={`${styles.eyebrow} text-uppercase small`}>{eyebrow}</div>
          <h2 className={`fw-bold ${styles.sectionTitle}`}>{sectionTitle}</h2>
          <p className={`${styles.subtitle} m-0`}>{sectionSubtitle}</p>
        </div>

        <div className="row g-4 align-items-center">
          {/* Left list */}
          <div className="col-12 col-lg-4">
            <div className={`pe-lg-3 ${styles.featuresCol}`}>
              {LEFT.map((f) => (
                <FeatureItem key={f.title} {...f} />
              ))}
            </div>
          </div>

          {/* Center visual */}
          <div className="col-12 col-lg-4 d-flex justify-content-center">
            <div className={styles.tray} data-reveal="zoom">
              <div className={`${styles.phone} ${styles.left}`}>
                <span className={styles.notch} aria-hidden></span>
                <div className={styles.screen}>
                  <Image
                    src="/assets/img/image.png"
                    alt="app preview left"
                    fill
                    sizes="(min-width: 992px) 240px, 40vw"
                    priority
                  />
                </div>
              </div>
              <div className={`${styles.phone} ${styles.right}`}>
                <span className={styles.notch} aria-hidden></span>
                <div className={styles.screen}>
                  <Image
                    src="/assets/img/girls_slay_mobile2.png"
                    alt="app preview right"
                    fill
                    sizes="(min-width: 992px) 240px, 40vw"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right list */}
          <div className="col-12 col-lg-4">
            <div className={`ps-lg-3 ${styles.featuresCol}`}>
              {RIGHT.map((f) => (
                <FeatureItem key={f.title} {...f} />
              ))}
            </div>
          </div>
        </div>
        {/* CTA */}
        <div className="text-center mt-4">
          <Link href="/contact" className={styles.ctaBtn} aria-label="Contact our experts">
            Talk to Our Experts
            <i className="fa-solid fa-arrow-right ms-2" aria-hidden></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
